import { useEffect, useMemo, useState } from 'react'
import { Link, useLocation, useSearchParams } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Edit, Eye, LayoutGrid, List, MoreHorizontal, Plus, Search, Trash2, Wrench } from 'lucide-react'
import { toast } from 'sonner'

import { workersApi, type WorkerSkill, type WorkerSkillPayload } from '@/services/workers'
import { payTypesApi } from '@/services/pay-types'
import { catalogRolesApi } from '@/services/catalog-roles'
import { categoriesApi } from '@/services/categories'
import { WorkerLifecycleNav } from '@/components/workers/worker-lifecycle-nav'
import { WorkerSkillsGroupCard, WorkerSkillsGroupCardSkeleton } from '@/components/workers/worker-skills-group-card'
import { SearchableSelect } from '@/components/common/searchable-select'
import { getServiceInstanceIdFromSearch, withServiceInstanceId } from '@/lib/service-instance'
import { unmaskMaskedId } from '@/lib/backend-ids'
import { resolveViewModeFromLocation, syncViewModeSearchParam } from '@/lib/list-view-mode'
import { getWorkerAvatarUrl, getWorkerDisplayName, getWorkerInitials } from '@/lib/worker-list-display'
import { getErrorMessage } from '@/lib/api'
import { cn, formatCurrency } from '@/lib/utils'
import type { Worker } from '@/types'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Checkbox } from '@/components/ui/checkbox'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import { Skeleton } from '@/components/ui/skeleton'
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from '@/components/ui/sheet'

type WorkerSkillFormState = {
  id: string | null
  worker: string
  skill_role: string
  job_category: string
  pay_type: string
  base_pay: string
  night_pay: string
  holiday_pay: string
  peak_pay: string
  active_from: string
  active_to: string
  is_primary: boolean
  is_active: boolean
  notes: string
}

const initialFormState: WorkerSkillFormState = {
  id: null,
  worker: '',
  skill_role: '',
  job_category: '',
  pay_type: '',
  base_pay: '',
  night_pay: '',
  holiday_pay: '',
  peak_pay: '',
  active_from: '',
  active_to: '',
  is_primary: false,
  is_active: true,
  notes: '',
}

const toOptionalPayNumber = (value?: string) => {
  if (!value || !value.trim()) {
    return undefined
  }

  const parsed = Number(value)
  return Number.isFinite(parsed) ? parsed : undefined
}

const appendPayScaleFields = (
  payload: WorkerSkillPayload,
  formState: WorkerSkillFormState,
) => {
  if (!formState.pay_type) {
    return payload
  }

  const basePay = toOptionalPayNumber(formState.base_pay)
  if (basePay === undefined) {
    return payload
  }

  payload.base_pay = basePay
  payload.night_pay = toOptionalPayNumber(formState.night_pay) ?? 0
  payload.holiday_pay = toOptionalPayNumber(formState.holiday_pay) ?? 0
  payload.peak_pay = toOptionalPayNumber(formState.peak_pay) ?? 0
  return payload
}

const validatePayFields = (formState: WorkerSkillFormState) => {
  const hasPayValues = [formState.base_pay, formState.night_pay, formState.holiday_pay, formState.peak_pay]
    .some((value) => value.trim() !== '')

  if (!formState.pay_type && !hasPayValues) {
    return null
  }

  if (!formState.pay_type) {
    return 'Pay type is required when pay details are provided'
  }

  if (!formState.base_pay.trim()) {
    return 'Base pay is required when pay type is selected'
  }

  const basePay = Number(formState.base_pay)
  const nightPay = formState.night_pay.trim() ? Number(formState.night_pay) : 0
  const holidayPay = formState.holiday_pay.trim() ? Number(formState.holiday_pay) : 0
  const peakPay = formState.peak_pay.trim() ? Number(formState.peak_pay) : 0

  if (!Number.isFinite(basePay) || basePay < 0) {
    return 'Base pay must be a valid non-negative number'
  }

  if (![nightPay, holidayPay, peakPay].every((value) => Number.isFinite(value) && value >= 0)) {
    return 'Additional pay values must be valid non-negative numbers'
  }

  return null
}

const formatWorkerSkillPay = (value?: string | number | null) => {
  if (value == null || String(value).trim() === '') {
    return null
  }

  const parsed = Number(value)
  if (Number.isFinite(parsed)) {
    return formatCurrency(parsed)
  }

  return String(value)
}

const WORKER_SKILLS_VIEW_STORAGE_KEY = 'worker-skills-view-mode'
type WorkerSkillsViewMode = 'list' | 'cards'
const WORKER_SKILLS_VIEW_DEFAULT: WorkerSkillsViewMode = 'list'

const getWorkerFilterKey = (worker: { id: string | number; worker_id?: string | number }) => {
  if (worker.worker_id != null && String(worker.worker_id).trim()) {
    return String(worker.worker_id)
  }
  return String(worker.id)
}

const rawWorkerUserId = (worker: { user?: string | number | null; user_id?: string | number | null }) => {
  const linkedUser = worker.user ?? worker.user_id
  return linkedUser == null ? undefined : String(linkedUser).trim() || undefined
}

export default function WorkerSkillsPage() {
  const location = useLocation()
  const [searchParams, setSearchParams] = useSearchParams()
  const queryClient = useQueryClient()
  const [search, setSearch] = useState(() => searchParams.get('q') ?? '')
  const [workerFilter, setWorkerFilter] = useState(() => searchParams.get('worker') ?? 'all')
  const [roleFilter, setRoleFilter] = useState(() => searchParams.get('role') ?? 'all')
  const [formOpen, setFormOpen] = useState(false)
  const [formState, setFormState] = useState<WorkerSkillFormState>(initialFormState)
  const [deleteTarget, setDeleteTarget] = useState<{ id: string | number; label: string } | null>(null)
  const [detailTarget, setDetailTarget] = useState<WorkerSkill | null>(null)
  const resolvedViewMode = useMemo(
    () => resolveViewModeFromLocation(
      location.search,
      WORKER_SKILLS_VIEW_STORAGE_KEY,
      ['list', 'cards'] as const,
      WORKER_SKILLS_VIEW_DEFAULT,
    ),
    [location.search],
  )
  const [viewMode, setViewMode] = useState<WorkerSkillsViewMode>(resolvedViewMode)

  const serviceInstanceId = getServiceInstanceIdFromSearch(location.search)
  const catalogRolesHref = withServiceInstanceId('/org/catalog-roles', serviceInstanceId)
  const searchParamsText = searchParams.toString()

  const workersQuery = useQuery({
    queryKey: ['workers', 'worker-skills', serviceInstanceId],
    queryFn: () => workersApi.getWorkers({ page_size: 300, service_instance_id: serviceInstanceId ?? undefined }),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  })

  const workerSkillsQuery = useQuery({
    queryKey: ['worker-skills', serviceInstanceId],
    queryFn: () => workersApi.getWorkerSkills({ service_instance_id: serviceInstanceId ?? undefined, page_size: 300 }),
    enabled: Boolean(serviceInstanceId),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  })

  const rolesQuery = useQuery({
    queryKey: ['catalog-roles', 'worker-skills', serviceInstanceId],
    queryFn: () => catalogRolesApi.list({ serviceInstanceId, includeHidden: true }),
    enabled: Boolean(serviceInstanceId),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  })

  const categoriesQuery = useQuery({
    queryKey: ['job-categories', 'worker-skills'],
    queryFn: () => categoriesApi.getCategories(),
    staleTime: 0,
    refetchOnMount: 'always',
    refetchOnWindowFocus: true,
  })

  const payTypesQuery = useQuery({
    queryKey: ['pay-type-choices'],
    queryFn: () => payTypesApi.getChoices(),
    staleTime: 60_000,
  })

  const payTypeOptions = payTypesQuery.data ?? []

  const workers = workersQuery.data?.results ?? []
  const roles = rolesQuery.data ?? []

  const workerSelectOptions = useMemo(
    () => workers.map((worker) => ({
      value: String(worker.id),
      label: worker.display_name || worker.full_name || `Worker ${worker.id}`,
      keywords: `${worker.display_name || ''} ${worker.full_name || ''} ${worker.email || ''} ${worker.id}`.trim(),
    })),
    [workers],
  )

  const workerFilterOptions = useMemo(
    () => [
      { value: 'all', label: 'All workers', keywords: 'all workers' },
      ...workers.map((worker) => {
        const filterKey = getWorkerFilterKey(worker)
        return {
          value: filterKey,
          label: worker.display_name || worker.full_name || `Worker ${worker.id}`,
          keywords: `${worker.display_name || ''} ${worker.full_name || ''} ${worker.email || ''} ${filterKey}`.trim(),
        }
      }),
    ],
    [workers],
  )

  const categories = categoriesQuery.data?.results ?? []
  const selectedWorker = useMemo(
    () => workers.find((worker) => String(worker.id) === formState.worker) ?? null,
    [formState.worker, workers],
  )
  // Show all categories regardless of selected worker
  const availableCategories = categories;

  const roleOptions = useMemo(() => {
    const optionMap = new Map<string, string>()

    roles.forEach((role: { id?: string | number | null; name?: string | null }) => {
      if (!role?.name) return
      if (role.id != null && String(role.id).trim()) {
        optionMap.set(String(role.id), role.name)
      }
    })

    return Array.from(optionMap.entries()).map(([id, name]) => ({ id, name }))
  }, [roles])

  const getRoleLabel = (row: WorkerSkill): string | number => {
    const raw = row as WorkerSkill & {
      skill_role_name?: string | null
      role_name?: string | null
      skill_role_label?: string | null
    }

    const fallback = String(raw.skill_role_name ?? raw.role_name ?? raw.skill_role_label ?? '').trim()
    if (fallback) return fallback

    const resolved = roleMap.get(String(row.skill_role))
    if (resolved) return resolved

    return row.skill_role
  }

  const workerMap = useMemo(() => {
    const byNumericId = new Map<string, string>()
    const byEncryptedId = new Map<string, string>()

    workers.forEach((worker) => {
      const label = worker.display_name || worker.full_name || `Worker ${worker.id}`
      byEncryptedId.set(String(worker.id), label)
      byNumericId.set(getWorkerFilterKey(worker), label)
    })

    return { byNumericId, byEncryptedId }
  }, [workers])

  const workerLinkMap = useMemo(() => {
    const map = new Map<string, string>()

    workers.forEach((worker) => {
      const hrefId = String(worker.id)
      map.set(String(worker.id), hrefId)
      map.set(getWorkerFilterKey(worker), hrefId)
      if (worker.worker_id != null && String(worker.worker_id).trim()) {
        map.set(String(worker.worker_id), hrefId)
      }
    })

    return map
  }, [workers])

  const workerProfileMap = useMemo(() => {
    const map = new Map<string, { name: string; avatar?: string; href?: string }>()

    workers.forEach((worker) => {
      const profile = {
        name: getWorkerDisplayName(worker),
        avatar: getWorkerAvatarUrl(worker),
        href: withServiceInstanceId(`/workers/${worker.id}`, serviceInstanceId),
      }
      const profileKeys = new Set<string>()

      const addKey = (value: unknown) => {
        const key = value == null ? '' : String(value).trim()
        if (!key || profileKeys.has(key)) {
          return
        }
        profileKeys.add(key)
        map.set(key, profile)
      }

      addKey(worker.id)
      addKey(getWorkerFilterKey(worker))
      addKey(worker.worker_id)
      addKey(unmaskMaskedId(worker.id))
      addKey(unmaskMaskedId(worker.worker_id))
      addKey((worker as Worker & { _id?: string | number })._id)
      addKey(rawWorkerUserId(worker))
    })

    return map
  }, [serviceInstanceId, workers])
  const roleMap = useMemo(
    () => new Map(roleOptions.map((option) => [option.id, option.name])),
    [roleOptions],
  )
  const categoryMap = useMemo(
    () => new Map(categories.map((category) => [String(category.id), category.name])),
    [categories],
  )

  /** Role filter values must match WorkerSkill.skill_role (numeric FK), not catalog encrypted ids. */
  const roleFilterOptions = useMemo(() => {
    const optionMap = new Map<string, string>()

    for (const row of workerSkillsQuery.data ?? []) {
      if (row.is_active === false) {
        continue
      }
      const roleId = String(row.skill_role ?? '').trim()
      if (!roleId) {
        continue
      }
      if (!optionMap.has(roleId)) {
        optionMap.set(roleId, String(getRoleLabel(row)))
      }
    }

    return Array.from(optionMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((left, right) => left.name.localeCompare(right.name))
  }, [roleMap, workerSkillsQuery.data])

  const filteredSkills = useMemo(() => {
    const rows = workerSkillsQuery.data ?? []
    const q = search.trim().toLowerCase()

    return rows.filter((row) => {
      if (row.is_active === false) {
        return false
      }

      const rowWorkerId = String(row.worker ?? '')
      if (workerFilter !== 'all' && rowWorkerId !== workerFilter) {
        return false
      }
      const rowRoleId = String(row.skill_role ?? '')
      const rowRoleUniqueId = String(row.skill_role_unique_id ?? '')
      if (
        roleFilter !== 'all'
        && rowRoleId !== roleFilter
        && rowRoleUniqueId !== roleFilter
      ) {
        return false
      }
      if (!q) {
        return true
      }

      const workerLabel = String(
        workerMap.byNumericId.get(rowWorkerId)
          ?? workerMap.byEncryptedId.get(rowWorkerId)
          ?? row.worker
          ?? '',
      ).toLowerCase()
      const roleLabel = String(getRoleLabel(row) ?? '').toLowerCase()
      const categoryLabel = String(categoryMap.get(String(row.job_category ?? '')) ?? '').toLowerCase()
      return workerLabel.includes(q) || roleLabel.includes(q) || categoryLabel.includes(q)
    })
  }, [categoryMap, roleFilter, search, workerFilter, workerMap, workerSkillsQuery.data])

  const resolveWorkerGroupKey = (workerId: string) => workerLinkMap.get(workerId) ?? workerId

  const formatRoleCategoryLabel = (roleLabel: string, categoryLabel: string) => {
    if (!categoryLabel || categoryLabel === '-') {
      return roleLabel
    }
    return `${roleLabel} · ${categoryLabel}`
  }

  const getWorkerLabelForSkill = (row: WorkerSkill) => {
    const workerId = String(row.worker ?? '')
    return workerProfileMap.get(workerId)?.name
      ?? workerMap.byNumericId.get(workerId)
      ?? workerMap.byEncryptedId.get(workerId)
      ?? workerId
  }

  const resolveWorkerProfileForSkill = (row: WorkerSkill | undefined) => {
    if (!row) {
      return undefined
    }

    const workerId = String(row.worker ?? '')
    const candidateKeys = [
      workerId,
      unmaskMaskedId(workerId),
      workerLinkMap.get(workerId),
      resolveWorkerGroupKey(workerId),
    ]
      .map((value) => (value == null ? '' : String(value).trim()))
      .filter(Boolean)

    for (const key of candidateKeys) {
      const profile = workerProfileMap.get(key)
      if (profile) {
        return profile
      }
    }

    return undefined
  }

  const buildSkillGroupItem = (row: WorkerSkill) => {
    const roleLabel = String(getRoleLabel(row))
    const categoryLabel = row.job_category
      ? (categoryMap.get(String(row.job_category)) ?? String(row.job_category))
      : '-'

    return {
      skill: row,
      roleLabel,
      categoryLabel,
      roleCategoryLabel: formatRoleCategoryLabel(roleLabel, categoryLabel),
      payLabel: row.base_pay != null && String(row.base_pay).trim() !== ''
        ? `${row.pay_type_label || row.pay_type || 'Pay'} · ${formatWorkerSkillPay(row.base_pay)}`
        : (row.pay_type_label || row.pay_type || '-'),
    }
  }

  const skillsByWorker = useMemo(() => {
    const groups = new Map<string, { groupKey: string; workerId: string; skills: WorkerSkill[] }>()

    for (const row of filteredSkills) {
      const workerId = String(row.worker ?? '')
      const groupKey = resolveWorkerGroupKey(workerId)
      const current = groups.get(groupKey)
      if (current) {
        current.skills.push(row)
      } else {
        groups.set(groupKey, { groupKey, workerId, skills: [row] })
      }
    }

    return Array.from(groups.values())
      .map((group) => {
        const profile = resolveWorkerProfileForSkill(group.skills[0])
        const workerName = getWorkerLabelForSkill(group.skills[0])
        return {
          groupKey: group.groupKey,
          workerName,
          workerAvatar: profile?.avatar,
          workerHref: profile?.href
            ?? (workerLinkMap.get(String(group.skills[0]?.worker ?? ''))
              ? withServiceInstanceId(`/workers/${workerLinkMap.get(String(group.skills[0]?.worker ?? ''))}`, serviceInstanceId)
              : null),
          items: group.skills
            .map(buildSkillGroupItem)
            .sort((left, right) => left.roleCategoryLabel.localeCompare(right.roleCategoryLabel)),
        }
      })
      .sort((left, right) => left.workerName.localeCompare(right.workerName))
  }, [categoryMap, filteredSkills, serviceInstanceId, workerLinkMap, workerProfileMap, workerMap])

  const createMutation = useMutation({
    mutationFn: (payload: WorkerSkillPayload) => workersApi.createWorkerSkill(payload),
    onSuccess: async () => {
      toast.success('Worker skill created')
      setFormOpen(false)
      setFormState(initialFormState)
      await queryClient.invalidateQueries({ queryKey: ['worker-skills'] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: string | number; payload: Partial<WorkerSkillPayload> }) => workersApi.updateWorkerSkill(id, payload),
    onSuccess: async () => {
      toast.success('Worker skill updated')
      setFormOpen(false)
      setFormState(initialFormState)
      await queryClient.invalidateQueries({ queryKey: ['worker-skills'] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: string | number) => workersApi.deleteWorkerSkill(id),
    onSuccess: async () => {
      toast.success('Worker skill removed')
      setDeleteTarget(null)
      await queryClient.invalidateQueries({ queryKey: ['worker-skills'] })
    },
    onError: (error) => toast.error(getErrorMessage(error)),
  })

  const isSaving = createMutation.isPending || updateMutation.isPending

  const openCreate = () => {
    setFormState(initialFormState)
    setFormOpen(true)
  }

  const openEdit = (row: any) => {
    setFormState({
      id: String(row.id),
      worker: String(row.worker ?? ''),
      skill_role: String(row.skill_role ?? ''),
      job_category: row.job_category != null ? String(row.job_category) : '',
      pay_type: String(row.pay_type ?? ''),
      base_pay: row.base_pay != null ? String(row.base_pay) : '',
      night_pay: row.night_pay != null ? String(row.night_pay) : '',
      holiday_pay: row.holiday_pay != null ? String(row.holiday_pay) : '',
      peak_pay: row.peak_pay != null ? String(row.peak_pay) : '',
      active_from: String(row.active_from ?? ''),
      active_to: String(row.active_to ?? ''),
      is_primary: Boolean(row.is_primary),
      is_active: row.is_active !== false,
      notes: String(row.notes ?? ''),
    })
    setFormOpen(true)
  }

  const buildPayload = (): WorkerSkillPayload => {
    const payload: WorkerSkillPayload = {
      worker: formState.worker,
      skill_role: formState.skill_role,
      is_primary: formState.is_primary,
      is_active: formState.is_active,
    }

    if (formState.job_category) payload.job_category = formState.job_category
    if (formState.pay_type) payload.pay_type = formState.pay_type as WorkerSkillPayload['pay_type']
    if (formState.active_from) payload.active_from = formState.active_from
    if (formState.active_to) payload.active_to = formState.active_to
    if (formState.notes.trim()) payload.notes = formState.notes.trim()

    return appendPayScaleFields(payload, formState)
  }

  const submitForm = async () => {
    if (!formState.worker || !formState.skill_role) {
      toast.error('Worker and skill role are required')
      return
    }

    if (formState.job_category && selectedWorker) {
      const workerCategoryIds = new Set((selectedWorker.categories ?? []).map((categoryId) => String(categoryId)))
      if (!workerCategoryIds.has(formState.job_category)) {
        toast.error('Select a category that the chosen worker already belongs to')
        return
      }
    }

    const payValidationError = validatePayFields(formState)
    if (payValidationError) {
      toast.error(payValidationError)
      return
    }

    const payload = buildPayload()
    if (formState.id) {
      await updateMutation.mutateAsync({ id: formState.id, payload })
      return
    }

    await createMutation.mutateAsync(payload)
  }

  const loading = workersQuery.isLoading || workerSkillsQuery.isLoading || rolesQuery.isLoading || categoriesQuery.isLoading || payTypesQuery.isLoading

  useEffect(() => {
    const currentParams = new URLSearchParams(searchParamsText)
    setSearch(currentParams.get('q') ?? '')
    setWorkerFilter(currentParams.get('worker') ?? 'all')
    setRoleFilter(currentParams.get('role') ?? 'all')
    setViewMode(resolveViewModeFromLocation(
      `?${searchParamsText}`,
      WORKER_SKILLS_VIEW_STORAGE_KEY,
      ['list', 'cards'] as const,
      WORKER_SKILLS_VIEW_DEFAULT,
    ))
  }, [searchParamsText])

  const handleViewModeChange = (nextMode: WorkerSkillsViewMode) => {
    setViewMode(nextMode)
    try {
      localStorage.setItem(WORKER_SKILLS_VIEW_STORAGE_KEY, nextMode)
    } catch {
      // Ignore storage failures.
    }

    const next = new URLSearchParams(searchParamsText)
    syncViewModeSearchParam(next, nextMode, WORKER_SKILLS_VIEW_DEFAULT)
    setSearchParams(next, { replace: true })
  }

  useEffect(() => {
    const next = new URLSearchParams(searchParamsText)

    if (search.trim()) {
      next.set('q', search.trim())
    } else {
      next.delete('q')
    }

    if (workerFilter !== 'all') {
      next.set('worker', workerFilter)
    } else {
      next.delete('worker')
    }

    if (roleFilter !== 'all') {
      next.set('role', roleFilter)
    } else {
      next.delete('role')
    }

    next.delete('status')

    const currentText = searchParamsText
    const nextText = next.toString()
    if (currentText !== nextText) {
      setSearchParams(next, { replace: true })
    }
  }, [roleFilter, search, searchParamsText, setSearchParams, workerFilter])

  const resetFilters = () => {
    setSearch('')
    setWorkerFilter('all')
    setRoleFilter('all')
  }

  return (
    <div className="space-y-6 p-4 md:p-6">
      <WorkerLifecycleNav activeStage="worker-skills" />

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5" />
            Worker Skills
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
            <div className="relative w-full md:max-w-sm">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input value={search} onChange={(event) => setSearch(event.target.value)} className="pl-9" placeholder="Search worker skills" />
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <div className="inline-flex h-9 rounded-lg border border-border/70 bg-background p-0.5">
                <button
                  type="button"
                  aria-pressed={viewMode === 'list'}
                  onClick={() => handleViewModeChange('list')}
                  className={cn(
                    'inline-flex h-full items-center gap-1.5 rounded-md px-2.5 text-xs font-medium transition',
                    viewMode === 'list' ? 'bg-muted text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  <List className="h-3.5 w-3.5" />
                  List
                </button>
                <button
                  type="button"
                  aria-pressed={viewMode === 'cards'}
                  onClick={() => handleViewModeChange('cards')}
                  className={cn(
                    'inline-flex h-full items-center gap-1.5 rounded-md px-2.5 text-xs font-medium transition',
                    viewMode === 'cards' ? 'bg-muted text-foreground shadow-sm' : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  <LayoutGrid className="h-3.5 w-3.5" />
                  Cards
                </button>
              </div>
              <Button variant="outline" onClick={resetFilters}>Reset Filters</Button>
              <Button variant="outline" asChild>
                <Link to={catalogRolesHref}>Role skills</Link>
              </Button>
              <Button onClick={openCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Add Worker Skill
              </Button>
            </div>
          </div>

          <div className="grid gap-3 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Filter by Worker</Label>
              <SearchableSelect
                ariaLabel="Filter by worker"
                value={workerFilter}
                onValueChange={setWorkerFilter}
                options={workerFilterOptions}
                placeholder="All workers"
                searchPlaceholder="Search workers"
                emptyText="No workers found."
              />
            </div>

            <div className="space-y-2">
              <Label>Filter by Skill Role</Label>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger>
                  <SelectValue placeholder="All roles" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All roles</SelectItem>
                  {roleFilterOptions.length === 0 ? (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">
                      No worker skill roles yet. Add a worker skill or create catalog roles first.
                    </div>
                  ) : (
                    roleFilterOptions.map((role) => (
                      <SelectItem key={role.id} value={role.id}>
                        {role.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
            </div>
          </div>

          {loading ? (
            viewMode === 'cards' ? (
              <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {Array.from({ length: 6 }).map((_, index) => (
                  <WorkerSkillsGroupCardSkeleton key={index} />
                ))}
              </div>
            ) : (
              <div className="space-y-2">
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
                <Skeleton className="h-10 w-full" />
              </div>
            )
          ) : viewMode === 'cards' ? (
            skillsByWorker.length > 0 ? (
              <div className="grid gap-2.5 sm:grid-cols-2 xl:grid-cols-3 2xl:grid-cols-4">
                {skillsByWorker.map((group) => (
                  <WorkerSkillsGroupCard
                    key={group.groupKey}
                    workerName={group.workerName}
                    workerAvatar={group.workerAvatar}
                    workerHref={group.workerHref}
                    skills={group.items}
                    onView={setDetailTarget}
                    onEdit={openEdit}
                    onDelete={(skill) => setDeleteTarget({
                      id: skill.id,
                      label: `${group.workerName} / ${String(getRoleLabel(skill))}`,
                    })}
                  />
                ))}
              </div>
            ) : (
              <div className="flex min-h-48 flex-col items-center justify-center rounded-2xl border border-dashed border-border/70 bg-muted/10 p-8 text-center text-muted-foreground">
                <Wrench className="mb-2 h-8 w-8 opacity-60" />
                <p className="font-medium text-foreground">No worker skills found</p>
                <p className="mt-1 text-xs">Adjust filters or add a worker skill mapping.</p>
              </div>
            )
          ) : (
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Worker</TableHead>
                    <TableHead>Skill · Category</TableHead>
                    <TableHead>Currency Pay</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {skillsByWorker.length > 0 ? skillsByWorker.flatMap((group) => {
                    const workerHrefId = workerLinkMap.get(String(group.items[0]?.skill.worker ?? ''))

                    return group.items.map((item, index) => {
                      const row = item.skill
                      return (
                        <TableRow key={String(row.id)}>
                          {index === 0 ? (
                            <TableCell rowSpan={group.items.length} className="align-top">
                              <div className="flex items-center gap-2.5 py-1">
                                <Avatar className="h-9 w-9 border border-border/60">
                                  <AvatarImage src={group.workerAvatar || undefined} alt={group.workerName} />
                                  <AvatarFallback className="bg-primary/10 text-[10px] font-semibold text-primary">
                                    {getWorkerInitials(group.workerName)}
                                  </AvatarFallback>
                                </Avatar>
                                <div className="min-w-0">
                                  {workerHrefId ? (
                                    <Link
                                      to={withServiceInstanceId(`/workers/${workerHrefId}`, serviceInstanceId)}
                                      className="block font-medium text-foreground transition-colors hover:text-primary hover:underline"
                                    >
                                      {group.workerName}
                                    </Link>
                                  ) : (
                                    <span className="font-medium text-foreground">{group.workerName}</span>
                                  )}
                                  <p className="text-[10px] text-muted-foreground">
                                    {group.items.length} skill{group.items.length === 1 ? '' : 's'}
                                  </p>
                                </div>
                              </div>
                            </TableCell>
                          ) : null}
                          <TableCell>{item.roleCategoryLabel}</TableCell>
                          <TableCell>{item.payLabel}</TableCell>
                          <TableCell className="text-right">
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="h-8 w-8">
                                  <MoreHorizontal className="h-4 w-4" />
                                  <span className="sr-only">Actions</span>
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="w-40">
                                <DropdownMenuItem onClick={() => setDetailTarget(row)}>
                                  <Eye className="mr-2 h-4 w-4" />
                                  View
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => openEdit(row)}>
                                  <Edit className="mr-2 h-4 w-4" />
                                  Edit
                                </DropdownMenuItem>
                                <DropdownMenuSeparator />
                                <DropdownMenuItem
                                  className="text-destructive focus:text-destructive"
                                  onClick={() => setDeleteTarget({
                                    id: row.id,
                                    label: `${group.workerName} / ${item.roleLabel}`,
                                  })}
                                >
                                  <Trash2 className="mr-2 h-4 w-4" />
                                  Remove
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </TableCell>
                        </TableRow>
                      )
                    })
                  }) : (
                    <TableRow>
                      <TableCell colSpan={4} className="h-20 text-center text-muted-foreground">
                        No worker skills found.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={formOpen} onOpenChange={setFormOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{formState.id ? 'Edit Worker Skill' : 'Add Worker Skill'}</DialogTitle>
            <DialogDescription>
              Configure worker-role skill mapping with optional pay overrides and active date range.
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 md:grid-cols-2">
            <div className="space-y-2">
              <Label>Worker</Label>
              <SearchableSelect
                ariaLabel="Worker"
                value={formState.worker}
                onValueChange={(value) => setFormState((prev) => ({ ...prev, worker: value }))}
                options={workerSelectOptions}
                placeholder="Select worker"
                searchPlaceholder="Search workers"
                emptyText="No workers found."
              />
            </div>

            <div className="space-y-2">
              <Label>Skill Role</Label>
              <Select
                value={formState.skill_role}
                disabled={!formState.worker}
                onValueChange={(value) => setFormState((prev) => ({ ...prev, skill_role: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={formState.worker ? 'Select role' : 'Select worker first'} />
                </SelectTrigger>
                <SelectContent>
                  {roleOptions.length === 0 ? (
                    <div className="px-2 py-1.5 text-sm text-muted-foreground">
                      No role skills found. Create one in Org {'>'} Role skills.
                    </div>
                  ) : (
                    roleOptions.map((role) => (
                      <SelectItem key={role.id} value={role.id}>
                        {role.name}
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                Choose a worker first, then pick the role skill to map.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Job Category (Optional)</Label>
              <Select
                value={formState.job_category || '__none__'}
                disabled={!formState.worker}
                onValueChange={(value) => setFormState((prev) => ({ ...prev, job_category: value === '__none__' ? '' : value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder={formState.worker ? 'Select category' : 'Select worker first'} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No category</SelectItem>
                  {availableCategories.map((category) => (
                    <SelectItem key={String(category.id)} value={String(category.id)}>
                      {category.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground">
                All-category skills apply to every job category and supersede category-specific skills for the same role.
              </p>
            </div>

            <div className="space-y-2">
              <Label>Pay Type</Label>
              <Select
                value={formState.pay_type || '__none__'}
                onValueChange={(value) => setFormState((prev) => ({ ...prev, pay_type: value === '__none__' ? '' : value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select pay type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">No pay type override</SelectItem>
                  {payTypeOptions.map((item) => (
                    <SelectItem key={item.value} value={item.value}>{item.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Base Pay</Label>
              <Input value={formState.base_pay} onChange={(event) => setFormState((prev) => ({ ...prev, base_pay: event.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Night Pay</Label>
              <Input value={formState.night_pay} onChange={(event) => setFormState((prev) => ({ ...prev, night_pay: event.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Holiday Pay</Label>
              <Input value={formState.holiday_pay} onChange={(event) => setFormState((prev) => ({ ...prev, holiday_pay: event.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Peak Pay</Label>
              <Input value={formState.peak_pay} onChange={(event) => setFormState((prev) => ({ ...prev, peak_pay: event.target.value }))} />
            </div>

            <div className="space-y-2">
              <Label>Active From</Label>
              <Input type="date" value={formState.active_from} onChange={(event) => setFormState((prev) => ({ ...prev, active_from: event.target.value }))} />
            </div>
            <div className="space-y-2">
              <Label>Active To</Label>
              <Input type="date" value={formState.active_to} onChange={(event) => setFormState((prev) => ({ ...prev, active_to: event.target.value }))} />
            </div>

            <div className="space-y-2 md:col-span-2">
              <Label>Notes</Label>
              <Input value={formState.notes} onChange={(event) => setFormState((prev) => ({ ...prev, notes: event.target.value }))} />
            </div>

            <div className="flex items-center gap-3">
              <Checkbox
                id="worker-skill-primary"
                checked={formState.is_primary}
                onCheckedChange={(checked) => setFormState((prev) => ({ ...prev, is_primary: checked === true }))}
              />
              <Label htmlFor="worker-skill-primary">Primary Skill</Label>
            </div>

            <div className="flex items-center gap-3">
              <Checkbox
                id="worker-skill-active"
                checked={formState.is_active}
                onCheckedChange={(checked) => setFormState((prev) => ({ ...prev, is_active: checked === true }))}
              />
              <Label htmlFor="worker-skill-active">Active</Label>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setFormOpen(false)} disabled={isSaving}>Cancel</Button>
            <Button onClick={submitForm} disabled={isSaving}>
              {isSaving ? 'Saving...' : formState.id ? 'Update Worker Skill' : 'Create Worker Skill'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet open={Boolean(detailTarget)} onOpenChange={(open) => { if (!open) setDetailTarget(null) }}>
        <SheetContent side="right" className="sm:max-w-lg">
          <SheetHeader>
            <SheetTitle>Worker Skill Details</SheetTitle>
            <SheetDescription>
              {detailTarget
                ? `${workerMap.byNumericId.get(String(detailTarget.worker)) ?? workerMap.byEncryptedId.get(String(detailTarget.worker)) ?? detailTarget.worker} / ${roleMap.get(String(detailTarget.skill_role)) ?? detailTarget.skill_role}`
                : 'Mapping details'}
            </SheetDescription>
          </SheetHeader>

          {detailTarget ? (
            <div className="grid grid-cols-1 gap-3 px-4 pb-6 text-sm">
              <div><span className="font-medium">Worker:</span> {workerMap.byNumericId.get(String(detailTarget.worker)) ?? workerMap.byEncryptedId.get(String(detailTarget.worker)) ?? detailTarget.worker}</div>
              <div><span className="font-medium">Skill Role:</span> {getRoleLabel(detailTarget)}</div>
              <div><span className="font-medium">Category:</span> {detailTarget.job_category ? (categoryMap.get(String(detailTarget.job_category)) ?? detailTarget.job_category) : '-'}</div>
              <div><span className="font-medium">Pay Type:</span> {detailTarget.pay_type_label || detailTarget.pay_type || '-'}</div>
              {/* Pay fields removed to match backend */}
              <div><span className="font-medium">Active From:</span> {detailTarget.active_from || '-'}</div>
              <div><span className="font-medium">Active To:</span> {detailTarget.active_to || '-'}</div>
              <div><span className="font-medium">Primary:</span> {detailTarget.is_primary ? 'Yes' : 'No'}</div>
              <div><span className="font-medium">Status:</span> {detailTarget.is_active === false ? 'Inactive' : 'Active'}</div>
              <div><span className="font-medium">Notes:</span> {detailTarget.notes?.trim() ? detailTarget.notes : '-'}</div>
            </div>
          ) : null}
        </SheetContent>
      </Sheet>

      <Dialog open={Boolean(deleteTarget)} onOpenChange={(open) => { if (!open) setDeleteTarget(null) }}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Remove Worker Skill</DialogTitle>
            <DialogDescription>
              This will remove skill mapping {deleteTarget?.label ? `"${deleteTarget.label}"` : ''}. This action cannot be undone.
              If the skill is used on job assignments or work days, deletion is blocked — edit the skill and uncheck Active to deactivate it instead.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteTarget(null)} disabled={deleteMutation.isPending}>Cancel</Button>
            <Button
              variant="destructive"
              disabled={deleteMutation.isPending || !deleteTarget}
              onClick={async () => {
                if (!deleteTarget) {
                  return
                }
                await deleteMutation.mutateAsync(deleteTarget.id)
              }}
            >
              {deleteMutation.isPending ? 'Removing...' : 'Remove'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
